/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { Car, Users, ThumbsUp, Search, Download } from "lucide-react";
import RenderAdminPage from "../RenderAdminPage";
import PageSkeleton from "../../Components/PageSkeleton";
import axiosInstance from "../../services/Axios/axios";

// Forms submitted from the public Auto Scheme site (autoscheme.upnmg.com).
// /auto-scheme/applications returns a plain array newest-first, so search and
// paging are client-side, same as the Clearance Dashboard.

const PAGE_SIZE = 10;

const COLUMNS = [
  ["Name", "name"],
  ["Staff ID", "staff_id"],
  ["Region", "region"],
  ["District", "district"],
  ["Facility", "facility"],
  ["Contact", "contact"],
  ["Membership", "membership_duration"],
  ["Aware", "aware_of_scheme"],
  ["Interested", "interested"],
  ["Budget", "budget_range"],
  ["Car Type", "car_type"],
  ["Car", "vehicle_name"],
];

const date = (value) =>
  value ? new Date(value).toLocaleDateString("en-GB") : "—";

function downloadCsv(rows) {
  const header = [...COLUMNS.map(([h]) => h), "Verified Member", "Submitted"];
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = rows.map((r) =>
    [
      ...COLUMNS.map(([, key]) => r[key]),
      r.is_member ? "Yes" : "No",
      date(r.created_at),
    ]
      .map(escape)
      .join(",")
  );
  const blob = new Blob([[header.map(escape).join(","), ...lines].join("\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `auto-scheme-applications-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function AutoSchemeApplications() {
  const [stats, setStats] = useState({ total: 0, interested: 0, members: 0 });
  const [applications, setApplications] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    axiosInstance
      .get("/auto-scheme/applications/stats")
      .then((res) => setStats(res.data || {}))
      .catch(() => {});

    axiosInstance
      .get("/auto-scheme/applications")
      .then((res) => setApplications(res.data || []))
      .catch(() => setApplications([]))
      .finally(() => setHasLoaded(true));
  }, []);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const filtered = useMemo(() => {
    if (!search) return applications;
    const q = search.toLowerCase();
    return applications.filter((a) =>
      ["name", "staff_id", "region", "district", "facility", "contact", "car_type", "vehicle_name"].some(
        (key) => a[key]?.toLowerCase().includes(q)
      )
    );
  }, [applications, search]);

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const cards = [
    {
      label: "Total Applications",
      value: stats.total ?? 0,
      icon: <Car size={20} />,
      tone: "text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/25",
    },
    {
      label: "Interested",
      value: stats.interested ?? 0,
      icon: <ThumbsUp size={20} />,
      tone: "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/25",
    },
    {
      label: "Verified Members",
      value: stats.members ?? 0,
      icon: <Users size={20} />,
      tone: "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/25",
    },
  ];

  return (
    <RenderAdminPage title="Auto Scheme">
      <p className="text-sm text-slate-500 dark:text-slate-400 -mt-4 mb-5">
        Forms submitted from autoscheme.upnmg.com. "Verified" means the staff ID
        matched a UPNMG member when the form was sent.
      </p>

      <div className="grid gap-4 mb-5 grid-cols-1 sm:grid-cols-3">
        {cards.map((card) => (
          <div
            key={card.label}
            className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 px-4 py-4 shadow-sm"
          >
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${card.tone}`}>
              {card.icon}
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                {card.label}
              </p>
              <h3 className="text-xl font-black text-slate-800 dark:text-slate-100 truncate">
                {card.value}
              </h3>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-4">
        <div className="relative w-full sm:w-96">
          <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, staff ID, region, facility…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-slate-700 dark:text-slate-200 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <button
          onClick={() => downloadCsv(filtered)}
          disabled={filtered.length === 0}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
        >
          <Download size={16} /> Export CSV
        </button>
      </div>

      {!hasLoaded && applications.length === 0 ? (
        <PageSkeleton cards={0} rows={8} cols={8} />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700">
                <tr>
                  {[...COLUMNS.map(([h]) => h), "Verified", "Submitted"].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                {pageItems.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length + 2} className="px-4 py-16 text-center">
                      <Car size={30} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                        {search ? "No matching applications" : "No applications yet"}
                      </p>
                    </td>
                  </tr>
                ) : (
                  pageItems.map((a) => (
                    <tr key={a._id} className="hover:bg-slate-50 dark:hover:bg-gray-700/40 transition-colors">
                      {COLUMNS.map(([, key], i) => (
                        <td
                          key={key}
                          className={`px-4 py-3 text-xs whitespace-nowrap ${
                            i === 0
                              ? "font-bold text-slate-800 dark:text-slate-100"
                              : "text-slate-600 dark:text-slate-300"
                          }`}
                        >
                          {a[key] || "—"}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-xs whitespace-nowrap">
                        {a.is_member ? (
                          <span className="font-bold text-emerald-700 dark:text-emerald-400">Yes</span>
                        ) : (
                          <span className="font-bold text-amber-600 dark:text-amber-400">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
                        {date(a.created_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-gray-700">
              <p className="text-xs text-slate-400 dark:text-slate-500">
                Page {page + 1} of {pageCount} · {filtered.length} total
              </p>
              <div className="flex gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-gray-700"
                >
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={page >= pageCount - 1}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 dark:border-gray-600 text-slate-600 dark:text-slate-300 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </RenderAdminPage>
  );
}

export default AutoSchemeApplications;
